import styled, { createGlobalStyle } from "styled-components";
import { transparentize } from "polished";
import { routes } from "routes";

import { Balance, Transaction, Currency } from "models";

import { useNavigate } from "hooks/use-navigate";
import { useBalance } from "services/balance";
import { useTranslation } from "services/i18n";
import { useTransactions, useTransactionsService } from "services/transactions";
import { useTrack } from "services/track";
import { useConfigService } from "services/config";
import { useEffect, useMemo } from "react";
import { useState } from "hooks/use-state";

import { Tabs, useTabs } from "components/tabs";
import { BalanceView } from "./balance";
import { TabDeposit } from "./tab-deposit";
import { TabWithdraw } from "./tab-withdraw";
import { TabTransactions } from "./tab-transactions";
import { FirstDepositView } from "../first-deposit";

import { IconFrame } from "components/icon-frame";
import { ReactComponent as BuyCryptoIcon } from "icons/buy-crypto.svg";
import { ReactComponent as BuyFiatIcon } from "icons/buy-fiat.svg";
import { ReactComponent as BuyArrowIcon } from "icons/buy-new-user-arrow.svg";
import { useCurrencies, useOrderCurrencies } from "../../services/currencies";
import { useApiService } from "../../services/api";

let redirected = false;

export type MainPageProps = {
  initialTab?: "txns" | "deposit" | "withdraw";
};

export function MainPage(props: MainPageProps) {
  const track = useTrack();
  const api = useApiService();
  const navigate = useNavigate();
  const balance = useBalance();
  const transactions = useTransactions();
  const transactionsService = useTransactionsService();
  const currencies = useCurrencies();
  const config = useConfigService();
  const currenciesForFiatPayment = useOrderCurrencies();

  useEffect(() => {
    transactionsService.initial?.then(transactions => {
      if (!redirected && transactions.length > 0 && transactions[0].showWaiting === true) {
        redirected = true;
        navigate(routes.buy.payment({ id: transactions[0].id }));
      }
    });
  }, []);

  const newUser = balance?.empty === true;

  function handleOpenTransaction(transaction: Transaction) {
    if (transaction.type === "buy") {
      navigate(routes.buy.transaction({ id: transaction.id }));
    } else {
      navigate(routes.transaction({ id: transaction.id }));
    }
  }

  async function handleCancelTransaction(transaction: Transaction) {
    switch (transaction.type) {
      case "buy":
        return await api.cancelBuyOrder(transaction.id);
      case "sell":
        return await api.cancelSellOrder(transaction.id);
      case "deposit":
      case "withdraw":
        return await api.cancelTransaction(transaction.id);
      default:
        return transaction satisfies never;
    }
  }

  function handleDeposit(currency?: Currency["code"]) {
    if (currency) {
      track("[Wallet] Deposit: currency selected", { currency });

      if (["TON", "JETTON"].includes(currency) && config.query.tonConnect) {
        navigate(routes.deposit.amount({ currency }));
      } else {
        navigate(routes.deposit.index({ currency }));
      }
    } else {
      track("[Wallet] Deposit: open");
      navigate(routes.deposit.currency());
    }
  }

  if (balance?.empty) {
    return (
      <FirstDepositView
        onCrypto={() => navigate(routes.deposit.currency())}
        onFiat={() => navigate(routes.buy.amount())}
      />
    );
  }

  return (
    <MainView
      balance={balance ?? undefined}
      currencies={currencies}
      currenciesForFiatPayment={currenciesForFiatPayment}
      transactions={transactions}
      newUser={newUser}
      onDeposit={handleDeposit}
      initialTab={props.initialTab}
      onWithdraw={currency => {
        track("[Wallet] Withdraw: currency selected", { currency });
        navigate(routes.withdraw.address({ currency }));
      }}
      onBuy={() => {
        track("[Wallet] Buy: open");
        navigate(routes.buy.amount());
      }}
      onSell={() => {
        track("[Wallet] Sell: open");
        navigate(routes.sell.amount());
      }}
      onTransactionClick={handleOpenTransaction}
      onTransactionCancel={handleCancelTransaction}
      onBalanceSettingsClick={() => navigate(routes.settings())}
    />
  );
}

type ViewProps = {
  balance?: Balance;
  currencies?: Currency[];
  currenciesForFiatPayment?: (Currency & { topBanks: string; valuePresets: number[] })[];
  transactions: null | Transaction[];
  newUser?: boolean;
  initialTab?: "txns" | "deposit" | "withdraw";
  onDeposit(code?: Currency["code"]): void;
  onWithdraw(code: Currency["code"]): void;
  onBuy(): void;
  onSell(): void;
  onTransactionClick(transaction: Transaction): void;
  onTransactionCancel(transaction: Transaction): Promise<void>;
  onBalanceSettingsClick?(): void;
};

export function MainView({
  balance,
  currencies,
  currenciesForFiatPayment,
  transactions,
  initialTab,
  newUser,
  onDeposit,
  onWithdraw,
  onBuy,
  onSell,
  onTransactionClick,
  onTransactionCancel,
  onBalanceSettingsClick,
}: ViewProps) {
  const t = useTranslation();

  const activeTab = useState<"txns" | "deposit" | "withdraw">(initialTab ?? "deposit");

  const tabs = useTabs(activeTab, [
    { id: "deposit" as const, label: t("ui", "wallet_tab_deposit") },
    { id: "withdraw" as const, label: t("ui", "wallet_tab_withdraw") },
    { id: "txns" as const, label: t("ui", "wallet_tab_txns") },
  ]);

  const fiatBanks = useMemo(() => {
    if (!balance) return undefined;
    if (!currenciesForFiatPayment) return undefined;

    return (
      currenciesForFiatPayment.find(c => c.code === balance.fiat.currency.code)?.topBanks ?? null
    );
  }, [balance?.fiat.currency.code, currenciesForFiatPayment]);

  return (
    <Container>
      <AppContainer style={{ flexGrow: newUser ? 0 : 1 }}>
        <GlobalStyles />
        <BalanceView balance={balance} onClick={onBalanceSettingsClick} />

        {!newUser && (
          <>
            <TabsContainer>
              <Tabs tabs={tabs} />
            </TabsContainer>
            {tabs.match({
              deposit: (
                <TabDeposit
                  currencies={currencies?.filter(currency => !currency.isFiat)}
                  onDepositWithCard={onBuy}
                  onDepositWithCrypto={onDeposit}
                  fiatBanks={fiatBanks}
                />
              ),
              withdraw: (
                <TabWithdraw
                  currencies={currencies?.filter(currency => currency.withdrawAllowed)}
                  onWithdrawWithCard={onSell}
                  onWithdrawWithCrypto={onWithdraw}
                  fiatBanks={fiatBanks}
                />
              ),
              txns: (
                <TabTransactions
                  transactions={transactions}
                  onClick={onTransactionClick}
                  onCancel={onTransactionCancel}
                />
              ),
            })}
          </>
        )}
      </AppContainer>
      {newUser && (
        <NewUserContainer>
          <Label>{t("ui", "wallet_new_user_payment_method")}</Label>
          <NewUserButton onClick={onBuy}>
            <IconFrame size={40}>
              <BuyFiatIcon />
            </IconFrame>
            <NewUserButtonLabel>
              {t("ui", "wallet_new_user_payment_method_fiat")}
            </NewUserButtonLabel>
            <NewUserButtonArrow />
          </NewUserButton>
          <NewUserButton onClick={() => onDeposit()}>
            <IconFrame size={40}>
              <BuyCryptoIcon />
            </IconFrame>
            <NewUserButtonLabel>
              {t("ui", "wallet_new_user_payment_method_crypto")}
            </NewUserButtonLabel>
            <NewUserButtonArrow />
          </NewUserButton>
        </NewUserContainer>
      )}
    </Container>
  );
}

const GlobalStyles = createGlobalStyle`
  body, html {
    background-color: ${props => props.theme.bgSecondary} !important;
    color: ${props => props.theme.text} !important;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  min-height: calc(100vh - 32px);
`;

const AppContainer = styled.div`
  margin: -16px -20px;
  padding: 16px 20px;

  background-color: ${props => props.theme.bg};
  border-bottom: 1px solid ${props => transparentize(0.9, props.theme.link)};

  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TabsContainer = styled.div`
  margin: 0 -20px 16px;
`;

const NewUserContainer = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  margin-left: -4px;
  margin-right: -4px;

  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.div`
  color: ${props => props.theme.hint};
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.125rem;
  letter-spacing: 0.26px;
  text-transform: uppercase;

  padding-left: 4px;
`;

const NewUserButton = styled.button`
  margin: 0;
  border: 0;
  cursor: pointer;
  text-align: left;

  display: flex;
  padding: 16px 8px 16px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: 14px;
  background: ${props => props.theme.bg};
`;

const NewUserButtonLabel = styled.div`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.3125rem;

  flex-grow: 1;
`;

const NewUserButtonArrow = styled(BuyArrowIcon)`
  color: ${props => props.theme.hint};
`;
